(function (angular) {
  "use strict";

  angular.module("Toyota.Services").service("Tfy", [
    "cookies",
    "localstorage",
    "Dealer",
    function (cookies, localstorage, dealer) {
      var TOYOTA_FOR_YOU_COOKIE_NAME_OBSOLETE = "toyota_for_you";
      var TOYOTA_FOR_YOU_COOKIE_NAME = "toyota_for_you_v2";
      var VEHICLE_MRU_MATERIAL_CODE_ALL_MODELS_LOCAL_STORAGE_KEY =
        "vehicle_mru_material_code_all_models";
      var MAX_MATERIAL_CODES = 10;

      /*
       * Used by `tyTfy` directive to provide a fallback materialCode to
       * DealerInventoryService when no other material code is available
       * (e.g. when the user has not interacted with a BEP or Pricing Page).
       */
      var VEHICLE_FALLBACK_MATERIAL_CODE_ALL_MODELS_LOCAL_STORAGE_KEY =
        "vehicle_fallback_material_code_all_models";

      var cache = {
        vehicleModel: null,
      };

      /*
       *  This function stores material code in this JSON format
       *  (for up to MAX_MATERIAL_CODES vehicles)
       *
       *  {
       *    "vehicles": [
       *    {
       *      "model": "Kluger",
       *      "materialCode": "9T870701AFB20218"
       *    },
       *    {
       *      "model": "HiAce",
       *      "materialCode": "9612180A0FA11058"
       *    },
       *    {
       *      "model": "GR86",
       *      "materialCode": "0A95060A0AM20DCK"
       *    }
       *  ],
       *  "dealer": {
       *      "dealerSapCode": "37425",
       *      "branchCode": "37426"
       *    }
       *  }
       */

      function updateTfyCookie() {
        var existingCookie = getTfyCookie();
        var existingDealer = existingCookie?.dealer ?? null;

        var allModels = getVehicleMruMaterialCodeAllModels();

        /*
         * Convert object of all keys (except the "expiry" key) to an array
         * sorted by timestamp, latest last, and take the last 5 entries.
         */
        var sortedVehicleMruMaterialCodeAllModels = Object.keys(allModels)
          .filter((key) => key !== "expiry")
          .sort(function (a, b) {
            return allModels[a].timestamp - allModels[b].timestamp;
          })
          .map(function (key) {
            return { model: key, materialCode: allModels[key].materialCode };
          })
          .slice(-MAX_MATERIAL_CODES);

        var jsonData = {
          vehicles: sortedVehicleMruMaterialCodeAllModels,
          dealer: existingDealer,
        };

        deleteTfyCookieWithDomain();
        setTfyCookieWithDomain(jsonData);
      }

      function updateDealerInTfyCookie(dealerSapCode, branchCode) {
        var innerDealerSapCode = dealerSapCode;
        var innerBranchCode = branchCode;

        if (!innerDealerSapCode || !innerBranchCode) {
          var dealerObject = dealer.getSavedFallbackToDefault();
          innerDealerSapCode = dealerObject?.branchCode ?? "";
          innerBranchCode = dealerObject?.branchCode ?? "";
        }

        var existingCookie = getTfyCookie();
        var existingVehicles = existingCookie?.vehicles ?? [];

        var jsonData = {
          vehicles: existingVehicles,
          dealer: {
            dealerSapCode: innerDealerSapCode,
            branchCode: innerBranchCode,
          },
        };

        deleteTfyCookieWithDomain();
        setTfyCookieWithDomain(jsonData);
      }

      function getTfyCookie() {
        var cookie = cookies.get(TOYOTA_FOR_YOU_COOKIE_NAME);
        if (!cookie) {
          return null;
        }

        return JSON.parse(cookie);
      }

      function setTfyCookieWithDomain(jsonData) {
        const expires = new Date();
        // Set expiration to 1 year from now
        expires.setFullYear(expires.getFullYear() + 1);

        // Convert the value to a JSON string
        const jsonValue = JSON.stringify(jsonData);

        const domain = ".toyota.com.au"; // won't work in local. To make it work in local, change domain to toyota10.local

        const cookieString = `${TOYOTA_FOR_YOU_COOKIE_NAME}=${encodeURIComponent(
          jsonValue
        )}; domain=${domain}; path=/; expires=${expires.toUTCString()}`;

        document.cookie = cookieString;
      }

      function deleteTfyCookieWithDomain() {
        const expires = new Date();
        // Set expiration to 1 second later to delete the .www.toyota.com.au cookie
        // Because setting to earlier expiration date doesn't remove the cookie.
        expires.setSeconds(expires.getSeconds() + 1);

        document.cookie = `${TOYOTA_FOR_YOU_COOKIE_NAME_OBSOLETE}=; path=/; expires=${expires.toUTCString()}`;

        document.cookie = `${TOYOTA_FOR_YOU_COOKIE_NAME_OBSOLETE}=; domain=${
          window.location.hostname
        }; path=/; expires=${expires.toUTCString()}`;

        cookies.removeWithDomain(
          TOYOTA_FOR_YOU_COOKIE_NAME_OBSOLETE,
          window.location.hostname
        );

        cookies.removeWithDomain(
          TOYOTA_FOR_YOU_COOKIE_NAME_OBSOLETE,
          ".toyota.com.au"
        );
      }

      function setCurrentPageVehicleModel(vehicleModel) {
        cache.vehicleModel = vehicleModel;
      }

      function getVehicleMruMaterialCodeAllModels() {
        return (
          localstorage.get(
            VEHICLE_MRU_MATERIAL_CODE_ALL_MODELS_LOCAL_STORAGE_KEY
          ) || {}
        );
      }

      function cacheMaterialCodeAndUpdateCookie(model, materialCode) {
        localstorage.set(
          VEHICLE_MRU_MATERIAL_CODE_ALL_MODELS_LOCAL_STORAGE_KEY,
          {
            ...getVehicleMruMaterialCodeAllModels(),
            [model]: {
              materialCode: materialCode,
              timestamp: new Date().getTime(),
            },
          }
        );

        updateTfyCookie();
      }

      function getVehicleFallbackMaterialCodeAllModels() {
        return (
          localstorage.get(
            VEHICLE_FALLBACK_MATERIAL_CODE_ALL_MODELS_LOCAL_STORAGE_KEY
          ) || {}
        );
      }

      function cacheFallbackMaterialCode(tfyModelName, materialCode) {
        localstorage.set(
          VEHICLE_FALLBACK_MATERIAL_CODE_ALL_MODELS_LOCAL_STORAGE_KEY,
          {
            ...getVehicleFallbackMaterialCodeAllModels(),
            [tfyModelName]: {
              materialCode: materialCode,
            },
          }
        );
      }

      function getCurrentPageVehicleModel() {
        return cache.vehicleModel;
      }

      function getCachedMaterialCode(tfyModelName) {
        return getVehicleMruMaterialCodeAllModels()[tfyModelName]?.materialCode;
      }

      function getCachedFallbackMaterialCode(tfyModelName) {
        return getVehicleFallbackMaterialCodeAllModels()[tfyModelName]
          ?.materialCode;
      }

      function ingestTfyVehicleModelData() {
        var el = document.querySelector("ty-tfy-current-page-vehicle-model");

        if (!el) {
          return;
        }

        var modelName = el.dataset.modelName || "";
        var tfyModelName = el.dataset.tfyModelName || "";
        var fallbackMaterialCode = el.dataset.fallbackMaterialCode || "";

        if (modelName) {
          setCurrentPageVehicleModel({
            modelName: modelName ?? "",
            tfyModelName: tfyModelName ?? "",
          });

          if (fallbackMaterialCode) {
            cacheFallbackMaterialCode(tfyModelName, fallbackMaterialCode);
          }
        }
      }

      ingestTfyVehicleModelData();

      return {
        updateDealerInTfyCookie: function (dealerSapCode, branchCode) {
          updateDealerInTfyCookie(dealerSapCode, branchCode);
        },
        getLatestVehicle: function () {
          var vehicleCookie = getTfyCookie();
          if (
            !vehicleCookie ||
            !vehicleCookie.vehicles ||
            vehicleCookie.vehicles.length === 0
          ) {
            return null;
          }

          return vehicleCookie.vehicles[vehicleCookie.vehicles.length - 1];
        },
        setCurrentPageVehicleModel: function (model) {
          setCurrentPageVehicleModel(model);
        },
        getCurrentPageVehicleModel: function () {
          return getCurrentPageVehicleModel();
        },
        cacheMaterialCodeAndUpdateCookie: function (
          tfyModelName,
          materialCode
        ) {
          cacheMaterialCodeAndUpdateCookie(tfyModelName, materialCode);
        },
        getCachedMaterialCode: function (tfyModelName) {
          return getCachedMaterialCode(tfyModelName);
        },
        cacheFallbackMaterialCode: function (tfyModelName, materialCode) {
          cacheFallbackMaterialCode(tfyModelName, materialCode);
        },
        getCachedFallbackMaterialCode: function (tfyModelName) {
          return getCachedFallbackMaterialCode(tfyModelName);
        },
      };
    },
  ]);
})(window.angular);
